import React, { useState, useRef, useCallback } from "react";
// @ts-ignore — qrcode has no bundled types in all versions
import QRCode from "qrcode";

interface QRGeneratorProps {
  onRunRecorded?: (input: Record<string, unknown>, output: unknown) => void;
}

export default function QRGeneratorTool({ onRunRecorded }: QRGeneratorProps) {
  const [text, setText] = useState("");
  const [errorCorrection, setErrorCorrection] = useState<"L" | "M" | "Q" | "H">("M");
  const [size, setSize] = useState(256);
  const [darkColor, setDarkColor] = useState("#000000");
  const [lightColor, setLightColor] = useState("#ffffff");
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const generate = useCallback(async () => {
    if (!text.trim()) {
      setGenError("Please enter text or a URL to encode.");
      return;
    }
    setGenerating(true);
    setGenError(null);
    try {
      const url: string = await QRCode.toDataURL(text, {
        errorCorrectionLevel: errorCorrection,
        width: size,
        color: { dark: darkColor, light: lightColor },
        margin: 2,
      });
      setDataUrl(url);
      onRunRecorded?.(
        { text, errorCorrection, size, darkColor, lightColor },
        { dataUrl: url.substring(0, 64) + "…" },
      );
    } catch (err: any) {
      setGenError(err?.message || "QR generation failed");
    } finally {
      setGenerating(false);
    }
  }, [text, errorCorrection, size, darkColor, lightColor, onRunRecorded]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "qrcode.png";
    a.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Text or URL to encode</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="https://9nd.org"
          className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Error Correction</label>
          <select
            value={errorCorrection}
            onChange={(e) => setErrorCorrection(e.target.value as "L" | "M" | "Q" | "H")}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            <option value="L">L — Low (7%)</option>
            <option value="M">M — Medium (15%)</option>
            <option value="Q">Q — Quality (25%)</option>
            <option value="H">H — High (30%)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Size (px)</label>
          <select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {[128, 256, 512, 1024].map((s) => (
              <option key={s} value={s}>{s}×{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Foreground</label>
          <input
            type="color"
            value={darkColor}
            onChange={(e) => setDarkColor(e.target.value)}
            className="w-full h-10 px-1 py-1 border rounded-lg cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
          <input
            type="color"
            value={lightColor}
            onChange={(e) => setLightColor(e.target.value)}
            className="w-full h-10 px-1 py-1 border rounded-lg cursor-pointer"
          />
        </div>
      </div>

      <button
        onClick={generate}
        disabled={generating || !text.trim()}
        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
      >
        {generating ? "Generating…" : "Generate QR Code"}
      </button>

      {genError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{genError}</div>
      )}

      {dataUrl && (
        <div className="flex flex-col items-center gap-4 mt-4">
          <img src={dataUrl} alt="Generated QR Code" className="border rounded-xl shadow-sm" style={{ maxWidth: `${Math.min(size, 300)}px` }} />
          <button
            onClick={download}
            className="bg-gray-900 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-700 transition text-sm"
          >
            Download PNG
          </button>
        </div>
      )}
    </div>
  );
}
